"use client";

import Link from "next/link";
import { useState, useRef, useEffect } from "react";
import { usePathname } from "next/navigation";

export default function ProductsMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const pathname = usePathname();
  
  // Track if component is mounted to prevent hydration mismatch
  const [isMounted, setIsMounted] = useState(false);
  
  // Add timer ref to handle hover intent
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Determine if any product page is active
  const isActive = pathname ? (
    pathname.startsWith("/i-motion-system") ||
    pathname.startsWith("/i-model-system")
  ) : false;

  // Add scroll event listener to match the layout component
  useEffect(() => {
    setIsMounted(true);
    
    if (typeof window === 'undefined') return;
    
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setScrolled(scrollPosition > 50);
    };

    window.addEventListener("scroll", handleScroll);
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Toggle menu on button click (especially for mobile)
  const handleMenuToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  // Handle mouse enter with immediate open
  const handleMouseEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setIsOpen(true);
  };

  // Handle mouse leave with delayed close
  const handleMouseLeave = () => {
    closeTimerRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 300); // 300ms delay before closing menu
  };

  return (
    <div
      ref={menuRef}
      className="relative inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        ref={buttonRef}
        onClick={handleMenuToggle}
        className="flex items-center space-x-1 text-base px-3 py-1 my-1 rounded-md transition-all duration-300 relative group"
        aria-expanded={isOpen}
      >
        <span className={isActive ? "text-[#38b5a0] font-semibold" : "text-[#1a2942] hover:text-[#38b5a0]"}>Products</span>
        
        {/* Ensure consistent SVG rendering */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className={`w-5 h-5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          key="dropdown-arrow"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
        
        {/* Fix the gradient underline to be consistent */}
        {isMounted && (
          <span
            className={`absolute bottom-0 left-0 w-full h-0.5 bg-[#32a895] transform origin-left transition-transform duration-300 ${
              isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
            }`}
          ></span>
        )}
      </button>

      {/* Fixed dropdown menu - improved mobile interaction */}
      <div
        className={`absolute z-[1000] left-0 top-full min-w-[256px] mt-2 bg-white backdrop-blur-sm rounded-lg shadow-lg border border-slate-200 transition-all duration-200 ${
          isOpen
            ? "opacity-100 visible transform translate-y-0"
            : "opacity-0 invisible transform -translate-y-2 pointer-events-none"
        }`}
      >
        <div className="p-4">
          <div className="mb-3 px-3 pb-2 text-sm font-semibold text-[#38b5a0] border-b border-slate-100">
            OUR SYSTEMS
          </div>
          <Link
            href="/i-motion-system"
            onClick={() => setIsOpen(false)}
            className="block px-3 py-3 hover:bg-slate-50 rounded-md transition-colors"
          >
            <div className="font-medium text-[#1a2942] hover:text-[#2389db] transition-colors">
              I-motion System
            </div>
            <div className="text-sm text-[#455a73]">
              Performance & Athletic Focus
            </div>
          </Link>
          <Link
            href="/i-model-system"
            onClick={() => setIsOpen(false)}
            className="block px-3 py-3 hover:bg-slate-50 rounded-md transition-colors"
          >
            <div className="font-medium text-[#1a2942] hover:text-[#e05c88] transition-colors">
              i-model System
            </div>
            <div className="text-sm text-[#455a73]">
              Aesthetic & Toning Focus
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}
